import { InputField } from '@shared/InputField'
import { Col, Form, Row } from 'antd'
import {
  CardCurrenyType,
  CardType,
  OrderDeliveryType,
  OrderType,
} from '../../../constants/fieldValues.constant'
import React, { useEffect } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import SelectGlobal from '@shared/Select'
import { useTranslation } from 'react-i18next'
import { excludedRegex } from '@shared/libs/inputRegex'
import { OrderSalaryCardType } from 'store/types/salaryCard'
import Dialog from '@shared/Dialog'
import {
  useCoreConfigQuery,
  useFetchBranchListQuery,
} from 'store/reducers/configs/configs.api'
import { useLazyFetchBusinessCardQuery } from 'store/reducers/salaryCards/businessCard.api'
import { useFetchCreateSalaryCardMutation } from 'store/reducers/salaryCards/salaryCardList.api'
import RadioField from '@shared/RadioField/RadioField'
import TrustedPersonForm from './TrustedPersonForm'

function CompanyInfoForm() {
  const { data: branchList, isSuccess } = useFetchBranchListQuery()

  const [
    fetchBusinessCards,
    {
      isError,
      data: businessCardList,
      isSuccess: businessCardSuccess,
      error,
      status: businessCardListStatus,
    },
  ] = useLazyFetchBusinessCardQuery()
  const [_, { isSuccess: createSalaryCardSuccess }] =
    useFetchCreateSalaryCardMutation({ fixedCacheKey: 'createSalaryCard' })
  const {
    data: orderNoInfo,
    currentData,
    isSuccess: orderNoInfoSuccess,
  } = useCoreConfigQuery()

  const { control, setValue, resetField, trigger } =
    useFormContext<OrderSalaryCardType>()
  const { t } = useTranslation()
  const cardCurrency = useWatch({ name: 'cardCurrency', control })
  const watchedValues = useWatch({
    control,
    defaultValue: {
      orderType: OrderType.ORDINARY,
      orderDeliveryType: OrderDeliveryType.BANK,
    },
    name: ['orderType', 'orderDeliveryType'],
  })

  const watchOrderBranch = useWatch({ name: 'orderBranch', control })

  useEffect(() => {
    watchedValues[1] === OrderDeliveryType.COURIER &&
      setValue('branchTransferred', '2', { shouldValidate: true }),
      watchedValues[0] === OrderType.URGENT &&
        watchedValues[1] === OrderDeliveryType.BANK &&
        setValue('branchTransferred', '2', { shouldValidate: true })
    if (
      watchedValues[0] === OrderType.ORDINARY &&
      watchedValues[1] === OrderDeliveryType.BANK
    ) {
      resetField('branchTransferred')
      trigger('branchTransferred')
    }
  }, [watchedValues])

  useEffect(() => {
    if (orderNoInfoSuccess || createSalaryCardSuccess) {
      setValue('orderBranch', currentData?.branchNo, {
        shouldValidate: true,
      })
    }
  }, [
    currentData,
    watchOrderBranch,
    orderNoInfoSuccess,
    createSalaryCardSuccess,
  ])

  useEffect(() => {
    if (cardCurrency) {
      fetchBusinessCards({ currency: cardCurrency })
    }
  }, [cardCurrency])

  useEffect(() => {
    if (isError) {
      Dialog.warning({
        title: t('common.attention'),
        modalType: 'failed',
        message: error?.data,
        okText: t('orders.salaryCardOrder.buttonsLabels.close'),
        afterClose() {
          setValue('cardCurrency', CardCurrenyType.AZN)
        },
      })
    }
  }, [businessCardListStatus])

  return (
    <Row gutter={24}>
      <Col span={7}>
        <Form
          labelCol={{ span: 10 }}
          wrapperCol={{ span: 24 }}
          layout="horizontal"
        >
          <Row
            gutter={[0, 20]}
            style={{
              boxSizing: 'border-box',
              backgroundColor: '#F3F6FA',
              borderRadius: 16,
              padding: 20,
              flexDirection: 'column',
            }}
          >
            <Controller
              name="cardCurrency"
              control={control}
              defaultValue={CardCurrenyType.AZN}
              render={({ field, fieldState: { error, invalid } }) => (
                <RadioField
                  {...field}
                  style={{ marginBottom: 0 }}
                  validateStatus={invalid ? 'error' : 'success'}
                  help={invalid ? error?.message : ''}
                  label={t('orders.salaryCardOrder.inputLabels.cardCurrency')}
                  radioOptions={Object.entries(CardCurrenyType).map(
                    ([value, title]) => ({
                      value,
                      title,
                    })
                  )}
                />
              )}
            />

            {businessCardSuccess && (
              <Controller
                name="cardType"
                control={control}
                defaultValue={CardType.VISA}
                render={({ field, fieldState: { invalid, error } }) => (
                  <RadioField
                    {...field}
                    style={{ marginBottom: 0 }}
                    validateStatus={invalid ? 'error' : 'success'}
                    help={invalid ? error?.message : ''}
                    label={t('orders.salaryCardOrder.inputLabels.cardType')}
                    radioOptions={Object.entries(CardType)
                      .reverse()
                      .map(([value, title]) => ({
                        value,
                        disabled: !businessCardList[value.toLowerCase()],
                        title: t(`orders.salaryCardOrder.constants.${title}`),
                      }))}
                  />
                )}
              />
            )}

            <Controller
              name="orderType"
              control={control}
              defaultValue={OrderType.ORDINARY}
              render={({ field, fieldState: { invalid, error } }) => (
                <RadioField
                  {...field}
                  style={{ marginBottom: 0 }}
                  validateStatus={invalid ? 'error' : 'success'}
                  help={invalid ? error?.message : ''}
                  label={t('orders.salaryCardOrder.inputLabels.orderTypes')}
                  radioOptions={Object.entries(OrderType).map(
                    ([value, title]) => ({
                      value,
                      disabled:
                        value === OrderType.URGENT && !orderNoInfo?.salaryCard.preparationTime.urgent.active,
                      title: t(`orders.salaryCardOrder.constants.${title}`),
                      ...(value === OrderType.URGENT && {
                        onClick: () =>
                          Dialog.info({
                            title: t('common.attention'),
                            message: t(
                              'orders.salaryCardOrder.constants.commissionFee'
                            ),
                            okText: t(
                              'orders.salaryCardOrder.buttonsLabels.close'
                            ),
                          }),
                      }),
                      // tooltip: value === OrderType.URGENT && (
                      //   <Tooltip
                      //     title={t(
                      //       'orders.salaryCardOrder.constants.commissionFee'
                      //     )}
                      //   >
                      //     <InfoCircleOutlined
                      //       style={{
                      //         fontSize: 16,
                      //         cursor: 'pointer',
                      //         color: '#015CD1',
                      //       }}
                      //     />
                      //   </Tooltip>
                      // ),
                    })
                  )}
                />
              )}
            />

            <Controller
              name="orderDeliveryType"
              control={control}
              defaultValue={OrderDeliveryType.BANK}
              render={({ field, fieldState: { invalid, error } }) => (
                <RadioField
                  {...field}
                  style={{ marginBottom: 0 }}
                  validateStatus={invalid ? 'error' : 'success'}
                  help={invalid ? error?.message : ''}
                  style={{ marginBottom: 0 }}
                  label={t('orders.salaryCardOrder.inputLabels.deliveryTypes')}
                  radioOptions={Object.entries(OrderDeliveryType).map(
                    ([value, title]) => ({
                      value,
                      disabled:
                        value === OrderDeliveryType.COURIER &&
                        !orderNoInfo?.salaryCard.orderDelivery.courier.active,
                      title: t(`orders.salaryCardOrder.constants.${title}`),
                      // tooltip: value === OrderDeliveryType.COURIER && (
                      //   <Tooltip
                      //     title={t('orders.salaryCardOrder.constants.onlyBaku')}
                      //   >
                      //     <InfoCircleOutlined
                      //       style={{
                      //         fontSize: 16,
                      //         cursor: 'pointer',
                      //         color: '#015CD1',
                      //       }}
                      //     />
                      //   </Tooltip>
                      // ),
                    })
                  )}
                />
              )}
            />
          </Row>
        </Form>
      </Col>
      <Col span={8}>
        <Form
          style={{ height: '100%' }}
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 24 }}
          layout="horizontal"
        >
          <Row
            gutter={[0, 24]}
            style={{
              height: '100%',
              boxSizing: 'border-box',
              backgroundColor: '#F3F6FA',
              borderRadius: 16,
              padding: 20,
              flexDirection: 'column',
            }}
          >
            {/* todo */}
            <Controller
              name="orderBranch"
              control={control}
              render={({
                field: { value, ...field },
                fieldState: { invalid, error },
              }) => {
                const val = isSuccess
                  ? branchList?.find((b) => b.value == value)?.label
                  : ''

                return (
                  <InputField
                    disabled
                    style={{ margin: 0 }}
                    validateStatus={invalid ? 'error' : 'validating'}
                    help={invalid ? error?.message : ''}
                    label={t('orders.salaryCardOrder.inputLabels.orderBranch')}
                    size="large"
                    placeholder={t(
                      'orders.salaryCardOrder.inputLabels.orderBranch'
                    )}
                    {...field}
                    value={val}
                  />
                )
              }}
            />
            {/* todo */}
            <Controller
              name="branchTransferred"
              control={control}
              render={({ field, fieldState: { invalid, error } }) => {
                return (
                  <SelectGlobal
                    style={{ margin: 0 }}
                    label={t(
                      'orders.salaryCardOrder.inputLabels.BranchTransferred'
                    )}
                    {...field}
                    allowClear={false}
                    disabled={
                      !(
                        watchedValues[0] === OrderType.ORDINARY &&
                        watchedValues[1] === OrderDeliveryType.BANK
                      )
                    }
                    placeholder={t(
                      'orders.salaryCardOrder.inputLabels.selectBranch'
                    )}
                    options={isSuccess ? branchList : []}
                    validateStatus={invalid ? 'error' : 'success'}
                    help={invalid ? t(error?.message) : ''}
                  />
                )
              }}
            />

            <Controller
              name="note"
              control={control}
              render={({ field, fieldState: { error, invalid } }) => (
                <InputField
                  mask={/[\s\S]*/}
                  maxLength={50}
                  maskOptions={{
                    prepare: (str) => (excludedRegex.test(str) ? '' : str),
                    commit: (value, masked) => {
                      masked._value = value
                    },
                  }}
                  // maxLength={50}
                  placeholder={t(
                    'orders.salaryCardOrder.inputLabels.InformationNotTransmittedBank'
                  )}
                  label={t('orders.salaryCardOrder.inputLabels.note')}
                  {...field}
                  validateStatus={invalid ? 'error' : 'success'}
                  help={invalid ? error?.message : ''}
                />
              )}
            />
          </Row>
        </Form>
      </Col>
      <Col span={9}>
        <TrustedPersonForm />
      </Col>
    </Row>
  )
}

export default CompanyInfoForm
